// DRPX Courier — Safe localStorage Wrapper

/**
 * Liest einen Wert aus localStorage. Gibt fallback zurück bei Fehler oder
 * wenn der Key nicht existiert.
 *
 * @param key      - localStorage Key
 * @param fallback - Optionaler Rückgabewert wenn Key nicht existiert oder
 *                   ein Fehler auftritt (z.B. localStorage gesperrt im
 *                   Inkognito-Modus mancher Browser).
 * @returns Gespeicherter Wert, fallback, oder `null`
 */
export function safeGetLS(key: string, fallback?: string): string | null {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? v : fallback !== undefined ? fallback : null;
  } catch (_e: unknown) {
    return fallback !== undefined ? fallback : null;
  }
}

/**
 * Schreibt einen Wert in localStorage. Gibt `false` zurück bei Fehler
 * (z.B. Quota überschritten oder localStorage gesperrt).
 */
export function safeSetLS(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('localStorage write failed:', msg);
    return false;
  }
}

/**
 * Entfernt einen Key aus localStorage.
 *
 * @returns `true` bei Erfolg
 */
export function safeRemoveLS(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (_e: unknown) {
    return false;
  }
}

/**
 * `safeParseJSON` — `JSON.parse` mit Crash-Schutz.
 * Gibt `fallback` zurück, wenn der String nicht parsebar ist.
 *
 * Mit Overloads typisiert, damit Caller bei gegebenem Fallback einen
 * garantiert non-null Return-Typ bekommen — und ohne Fallback einen
 * `T | null`. So müssen Caller mit Default keine unnötigen null-Checks
 * machen.
 */
// Overload 1: Mit Fallback → garantiert non-null Return
export function safeParseJSON<T>(str: string | null | undefined, fallback: T): T;
// Overload 2: Ohne Fallback → kann null sein
export function safeParseJSON<T = unknown>(str: string | null | undefined): T | null;
// Implementation
export function safeParseJSON<T>(
  str: string | null | undefined,
  fallback?: T,
): T | null {
  if (str === null || str === undefined) {
    return fallback !== undefined ? fallback : null;
  }
  try {
    return JSON.parse(str) as T;
  } catch (_e: unknown) {
    return fallback !== undefined ? fallback : null;
  }
}
